import {
  Autocomplete,
  AutocompleteOption,
  FormControl,
  FormHelperText,
  FormLabel,
  ListItemContent,
  ListItemDecorator,
  ListSubheader,
} from '@mui/joy';
import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';

import CaseType from '../../../enums/CaseType';
import { useGetSelectorCases } from '../../../../queries';

import {
  CaseIcon,
  ComplaintCaseIcon,
  InlineSpinner,
  InsuredCaseIcon,
  RepairCaseIcon,
} from '../../../icons/Icons';

const propTypes = {
  callback: PropTypes.func,
  label: PropTypes.string,
  multiple: PropTypes.bool,
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      type: PropTypes.string,
      value: PropTypes.string,
    }),
  ),
  repairOnly: PropTypes.bool,
  required: PropTypes.bool,
  showInactive: PropTypes.bool,
  caseType: PropTypes.string,
  skipInitialOnChange: PropTypes.bool,
};

const defaultProps = {
  label: null,
};

const FormikCaseSelector = ({
  callback = null,
  name,
  label,
  required = false,
  multiple = false,
  options = null,
  caseType = '',
  skipInitialOnChange = false,
}) => {
  const { selectorCasesFormatted, selectorCasesIsLoading } = useGetSelectorCases({
    inactive: !!options,
  });
  const [query, setQuery] = useState('');

  const { t } = useTranslation();

  const { values, touched, errors, setFieldValue } = useFormikContext();

  const getCaseIcon = (type) => {
    switch (type) {
      case CaseType.InsuranceClaim:
        return <InsuredCaseIcon />;
      case CaseType.Complaint:
        return <ComplaintCaseIcon />;
      case CaseType.Repair:
        return <RepairCaseIcon />;
      case CaseType.Case:
      default:
        return <CaseIcon />;
    }
  };

  const onChange = (value) => {
    if (callback) {
      callback(value);
    }
    setFieldValue(name, value ?? null);
  };

  const cases = options ?? selectorCasesFormatted;
  const filteredCases = useMemo(
    () =>
      cases.filter(
        (c) =>
          c.label.toLowerCase().includes(query.toLowerCase()) && (!caseType || c.type === caseType),
      ),
    [cases, query, caseType],
  );

  const getValue = () => {
    if (!cases) return null;
    if (values[name] == null) return null;
    const value = cases.find((c) => c.value === values[name]) ?? null;
    if (!value && !skipInitialOnChange) {
      onChange(null);
    }
    return value;
  };

  return (
    <FormControl>
      <FormLabel required={required}>{label}</FormLabel>
      <Autocomplete
        onChange={(e, target) => onChange(target?.value)}
        onInputChange={(e, target) => setQuery(target)}
        size="sm"
        value={getValue()}
        endDecorator={selectorCasesIsLoading ? <InlineSpinner /> : null}
        loading={selectorCasesIsLoading}
        placeholder={t('select.label')}
        options={
          filteredCases.length > 50
            ? [
                ...filteredCases.slice(0, 50),
                { label: t('moreResultsAreHiddenRefineFilter'), value: 0 },
              ]
            : filteredCases
        }
        renderOption={(props, option) => {
          if (option.label === t('moreResultsAreHiddenRefineFilter')) {
            return <ListSubheader disabled>{option.label}</ListSubheader>;
          }
          return (
            <AutocompleteOption {...props}>
              <ListItemDecorator>{getCaseIcon(option.type)}</ListItemDecorator>
              <ListItemContent className="pl-2">{option.label}</ListItemContent>
            </AutocompleteOption>
          );
        }}
        multiple={multiple}
      />
      {touched[name] && errors[name] && (
        <FormHelperText className="text-xs">{errors[name]}</FormHelperText>
      )}
    </FormControl>
  );
};

FormikCaseSelector.propTypes = propTypes;

FormikCaseSelector.defaultProps = defaultProps;

export default FormikCaseSelector;
